import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { ERC20_ABI, MIGRATION_THRESHOLD } from '../config/contracts';

interface TokenPreview {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  balance: string;
}

interface AddTokenModalProps {
  onClose: () => void;
  onAdd: (tokenAddress: string, initialAmount: string, description: string, website: string) => Promise<boolean>;
  account: string | null;
  provider: ethers.providers.Web3Provider | null;
  loading: boolean;
}

const AddTokenModal: React.FC<AddTokenModalProps> = ({
  onClose,
  onAdd,
  account,
  provider,
  loading,
}) => {
  const [tokenAddress, setTokenAddress] = useState('');
  const [initialAmount, setInitialAmount] = useState('');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [preview, setPreview] = useState<TokenPreview | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [step, setStep] = useState<'input' | 'confirm' | 'success'>('input');

  const fetchTokenInfo = useCallback(async (address: string) => {
    if (!ethers.utils.isAddress(address)) {
      setFetchError('无效的合约地址');
      setPreview(null);
      return;
    }
    setFetching(true);
    setFetchError('');
    try {
      const rpcProvider = provider || new ethers.providers.JsonRpcProvider('https://bsc-dataseed1.binance.org/');
      const contract = new ethers.Contract(address, ERC20_ABI, rpcProvider);
      const [name, symbol, decimals, totalSupply] = await Promise.all([
        contract.name(),
        contract.symbol(),
        contract.decimals(),
        contract.totalSupply(),
      ]);

      let balance = '0';
      if (account) {
        const bal = await contract.balanceOf(account);
        balance = parseFloat(ethers.utils.formatUnits(bal, decimals)).toFixed(4);
      }

      setPreview({
        address,
        name,
        symbol,
        decimals,
        totalSupply: parseFloat(ethers.utils.formatUnits(totalSupply, decimals)).toLocaleString(undefined, { maximumFractionDigits: 2 }),
        balance,
      });
    } catch (e) {
      setFetchError('无法读取代币信息，请确认是有效的 BEP-20 代币地址');
      setPreview(null);
    } finally {
      setFetching(false);
    }
  }, [provider, account]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (tokenAddress.length === 42) {
        fetchTokenInfo(tokenAddress);
      } else {
        setPreview(null);
        setFetchError('');
      }
    }, 600);
    return () => clearTimeout(t);
  }, [tokenAddress, fetchTokenInfo]);

  const handleSubmit = async () => {
    if (!preview || !initialAmount || parseFloat(initialAmount) <= 0) return;
    const ok = await onAdd(preview.address, initialAmount, description.trim(), website.trim());
    if (ok) {
      setStep('success');
    }
  };



  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>添加代币到曲线</h2>
            <p style={styles.modalSub}>输入已有的 BEP-20 代币合约地址</p>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {step === 'success' ? (
          <div style={styles.successBox}>
            <div style={styles.successIcon}>✅</div>
            <div style={styles.successTitle}>代币添加成功！</div>
            <div style={styles.successSub}>
              {preview?.symbol} 已成功添加到 Easy 曲线交易平台，<br />
              用户现在可以通过联合曲线买卖此代币。
            </div>
            <button style={styles.successBtn} onClick={onClose}>
              返回市场
            </button>
          </div>
        ) : (
          <>
            {/* Token Address Input */}
            <div style={styles.field}>
              <label style={styles.fieldLabel}>代币合约地址 (BSC)</label>
              <input
                type="text"
                style={{
                  ...styles.fieldInput,
                  borderColor: fetchError ? '#ff4d6d' : preview ? '#00e676' : '#1e2d4a',
                }}
                placeholder="0x..."
                value={tokenAddress}
                onChange={e => setTokenAddress(e.target.value.trim())}
              />
              {fetching && (
                <div style={styles.fetchingHint}>⟳ 获取代币信息...</div>
              )}
              {fetchError && (
                <div style={styles.errorHint}>⚠ {fetchError}</div>
              )}
            </div>

            {/* Token Preview */}
            {preview && (
              <div style={styles.previewBox}>
                <div style={styles.previewHeader}>
                  <div style={styles.previewIcon}>
                    {preview.symbol.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={styles.previewName}>{preview.name}</div>
                    <div style={styles.previewSymbol}>${preview.symbol}</div>
                  </div>
                  <div style={styles.verifiedBadge}>✓ 已验证</div>
                </div>
                <div style={styles.previewStats}>
                  {[
                    { label: '精度', value: `${preview.decimals} decimals` },
                    { label: '总供应', value: preview.totalSupply },
                    { label: '我的余额', value: `${preview.balance} ${preview.symbol}` },
                    { label: '网络', value: 'BSC Mainnet' },
                  ].map((item, i) => (
                    <div key={i} style={styles.previewStat}>
                      <span style={styles.previewStatLabel}>{item.label}</span>
                      <span style={styles.previewStatValue}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Initial Amount */}
            {preview && (
              <div style={styles.field}>
                <label style={styles.fieldLabel}>
                  注入初始代币数量（将锁入曲线合约）
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                  <div style={{ ...styles.inputWrap, flex: 1 }}>
                    <input
                      type="number"
                      style={styles.amtInput}
                      placeholder="例如: 1000000"
                      value={initialAmount}
                      onChange={e => setInitialAmount(e.target.value)}
                      min="0"
                    />
                    <span style={styles.amtUnit}>{preview.symbol}</span>
                  </div>
                  <button
                    type="button"
                    style={styles.allBtn}
                    onClick={() => setInitialAmount(preview.balance)}
                  >
                    全部
                  </button>
                </div>
                <div style={styles.fieldHint}>
                  你的余额: <strong>{preview.balance} {preview.symbol}</strong>
                </div>
                <div style={styles.fieldHint}>
                  ⚠ 代币将被锁入曲线合约，用户购买时会逐步释放
                </div>
                <div style={{ ...styles.fieldHint, color: '#ffd600', marginTop: 4 }}>
                  💎 确认添加时将自动从你的钱包扣除 <strong style={{ color: '#ffd600' }}>10,000 DUSD</strong> 作为上币费
                </div>
              </div>
            )}

            {/* Description */}
            {preview && (
              <div style={styles.field}>
                <label style={styles.fieldLabel}>代币简介 <span style={{ color: '#3d5070', fontWeight: 400 }}>（选填）</span></label>
                <textarea
                  style={styles.textareaInput}
                  placeholder="简单介绍一下这个代币的用途、背景或特色..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                />
                <div style={{ ...styles.fieldHint, textAlign: 'right' as const }}>
                  {description.length} / 500
                </div>
              </div>
            )}

            {/* Website */}
            {preview && (
              <div style={styles.field}>
                <label style={styles.fieldLabel}>官网链接 <span style={{ color: '#3d5070', fontWeight: 400 }}>（选填）</span></label>
                <input
                  type="url"
                  style={styles.fieldInput}
                  placeholder="https://example.com"
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                />
              </div>
            )}

            {/* Info Cards */}
            <div style={styles.infoCards}>
              {[
                { icon: '💎', title: '10,000 DUSD 上币费', desc: '添加代币需支付 10,000 DUSD 作为上币费用' },
                { icon: '📈', title: '联合曲线定价', desc: '价格随购买量自动上涨，早期购买者获益更大' },
                { icon: '💧', title: '自动迁移', desc: `当储备达 ${parseInt(MIGRATION_THRESHOLD).toLocaleString()} USDT 后自动将流动性注入 PancakeSwap` },
                { icon: '💰', title: '1.5% 手续费', desc: '每笔交易收取 1.5% 手续费，用于平台运营' },
              ].map((item, i) => (
                <div key={i} style={styles.infoCard}>
                  <span style={styles.infoCardIcon}>{item.icon}</span>
                  <div>
                    <div style={styles.infoCardTitle}>{item.title}</div>
                    <div style={styles.infoCardDesc}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Submit */}
            {!account ? (
              <div style={styles.noWalletHint}>请先连接钱包才能添加代币</div>
            ) : (
              <button
                style={{
                  ...styles.submitBtn,
                  opacity: !preview || !initialAmount || parseFloat(initialAmount) <= 0 || loading ? 0.5 : 1,
                }}
                onClick={handleSubmit}
                disabled={!preview || !initialAmount || parseFloat(initialAmount) <= 0 || loading}
              >
                {loading ? '处理中...' : `支付 10,000 DUSD 并添加 ${preview?.symbol || '代币'}`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modal: {
    background: '#0f1629',
    border: '1px solid #1e2d4a',
    borderRadius: 24,
    padding: '32px',
    width: '100%',
    maxWidth: 560,
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#e8eef7',
    margin: 0,
  },
  modalSub: {
    fontSize: 13,
    color: '#6b7d99',
    margin: '6px 0 0',
  },
  closeBtn: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid #1e2d4a',
    color: '#6b7d99',
    width: 36,
    height: 36,
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#6b7d99',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  fieldInput: {
    background: '#0a0e1a',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    color: '#e8eef7',
    fontSize: 14,
    padding: '14px 16px',
    outline: 'none',
    fontFamily: 'monospace',
    transition: 'border-color 0.2s',
  },
  textareaInput: {
    background: '#0a0e1a',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    color: '#e8eef7',
    fontSize: 14,
    padding: '14px 16px',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    lineHeight: '1.6',
    transition: 'border-color 0.2s',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  fetchingHint: {
    fontSize: 12,
    color: '#00d4ff',
  },
  errorHint: {
    fontSize: 12,
    color: '#ff4d6d',
  },
  previewBox: {
    background: 'rgba(0,230,118,0.04)',
    border: '1px solid rgba(0,230,118,0.2)',
    borderRadius: 14,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  previewIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #00d4ff, #7c4dff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 800,
    color: '#fff',
  },
  previewName: {
    fontSize: 16,
    fontWeight: 700,
    color: '#e8eef7',
  },
  previewSymbol: {
    fontSize: 12,
    color: '#6b7d99',
    marginTop: 2,
  },
  verifiedBadge: {
    marginLeft: 'auto',
    fontSize: 12,
    color: '#00e676',
    background: 'rgba(0,230,118,0.1)',
    border: '1px solid rgba(0,230,118,0.2)',
    borderRadius: 6,
    padding: '4px 10px',
    fontWeight: 600,
  },
  previewStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  previewStat: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  previewStatLabel: {
    fontSize: 10,
    color: '#3d5070',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  previewStatValue: {
    fontSize: 13,
    color: '#e8eef7',
    fontWeight: 600,
  },
  inputWrap: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  amtInput: {
    width: '100%',
    background: '#0a0e1a',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    color: '#e8eef7',
    fontSize: 18,
    fontWeight: 600,
    padding: '14px 100px 14px 16px',
    outline: 'none',
  },
  amtUnit: {
    position: 'absolute' as const,
    right: 14,
    fontSize: 13,
    color: '#6b7d99',
    fontWeight: 600,
  },
  allBtn: {
    background: 'rgba(0,212,255,0.1)',
    border: '1px solid rgba(0,212,255,0.3)',
    color: '#00d4ff',
    fontSize: 13,
    fontWeight: 600,
    padding: '0 18px',
    borderRadius: 12,
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
  },
  fieldHint: {
    fontSize: 12,
    color: '#6b7d99',
  },
  infoCards: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  infoCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 10,
  },
  infoCardIcon: {
    fontSize: 18,
    flexShrink: 0,
    marginTop: 1,
  },
  infoCardTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#e8eef7',
    marginBottom: 2,
  },
  infoCardDesc: {
    fontSize: 12,
    color: '#6b7d99',
    lineHeight: '1.5',
  },
  noWalletHint: {
    textAlign: 'center' as const,
    color: '#6b7d99',
    fontSize: 14,
    padding: '16px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 12,
    border: '1px solid #1e2d4a',
  },
  submitBtn: {
    width: '100%',
    background: 'linear-gradient(135deg, #00d4ff, #7c4dff)',
    border: 'none',
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    padding: '16px',
    borderRadius: 14,
    cursor: 'pointer',
    boxShadow: '0 0 20px rgba(0,212,255,0.3)',
    transition: 'opacity 0.2s',
  },
  successBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
    gap: 16,
    padding: '20px 0',
  },
  successIcon: {
    fontSize: 56,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 800,
    color: '#e8eef7',
  },
  successSub: {
    fontSize: 14,
    color: '#6b7d99',
    lineHeight: '1.7',
  },
  successBtn: {
    background: 'linear-gradient(135deg, #00d4ff, #7c4dff)',
    border: 'none',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    padding: '14px 40px',
    borderRadius: 12,
    cursor: 'pointer',
    marginTop: 8,
  },
};

export default AddTokenModal;
